import {useState, useCallback, useMemo} from 'react';

/**
 * Shared chart primitives. Monochrome, so identity is carried by TEXTURE on fills and
 * DASH PATTERN plus marker shape on lines — never by hue, which the palette does not
 * have. Every series is also in a legend, and the month table below the charts is the
 * table view the accessibility pass asks for.
 */
export const FILLS = [
	{id: 'solid', paint: {fill: '#000'}},
	{id: 'hatch', paint: {fill: 'url(#f-hatch)', stroke: '#000', strokeWidth: 0.8}},
	{id: 'cross', paint: {fill: 'url(#f-cross)', stroke: '#000', strokeWidth: 0.8}},
	{id: 'dots', paint: {fill: 'url(#f-dots)', stroke: '#000', strokeWidth: 0.8}},
	{id: 'grey', paint: {fill: '#8a8a8a'}},
	{id: 'open', paint: {fill: '#fff', stroke: '#000', strokeWidth: 1.2}},
	{id: 'light', paint: {fill: '#d9d9d9'}},
];
export const fillById = id => FILLS.find(f => f.id === id)?.paint ?? FILLS[0].paint;

/** Line identity without colour: dash pattern first, marker shape second. */
export const DASHES = [null, '6 4', '2 3', '8 3 2 3', '1 4'];

export function Defs() {
	return (
		<defs>
			<pattern id="f-hatch" width="4" height="4" patternUnits="userSpaceOnUse"
				patternTransform="rotate(45)">
				<rect width="4" height="4" fill="#fff" /><rect width="2" height="4" fill="#000" />
			</pattern>
			<pattern id="f-cross" width="4" height="4" patternUnits="userSpaceOnUse"
				patternTransform="rotate(135)">
				<rect width="4" height="4" fill="#fff" /><rect width="1.5" height="4" fill="#000" />
			</pattern>
			<pattern id="f-dots" width="4" height="4" patternUnits="userSpaceOnUse">
				<rect width="4" height="4" fill="#fff" /><circle cx="2" cy="2" r="1" fill="#000" />
			</pattern>
		</defs>
	);
}

/** A "nice" axis step, so gridlines land on round numbers at any scale. */
export function niceScale(lo, hi, targetLines = 6) {
	if (hi === lo) { hi = lo + 1; }
	const raw = (hi - lo) / targetLines;
	const mag = 10 ** Math.floor(Math.log10(Math.abs(raw) || 1));
	const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw) ?? mag * 10;
	return {lo: Math.floor(lo / step) * step, hi: Math.ceil(hi / step) * step, step};
}

export const fmtTick = v =>
	Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `${Math.round(v / 1e3)}k` : String(v);

export function Grid({lo, hi, step, y, L, R, W, pct}) {
	const lines = useMemo(() => {
		const out = [];
		for (let v = lo; v <= hi + step / 2; v += step) out.push(Number(v.toFixed(6)));
		return out;
	}, [lo, hi, step]);
	return lines.map(v => (
		<g key={v}>
			<line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className={v === 0 ? 'zl' : 'gl'} />
			<text x={L - 6} y={y(v) + 3.5} className="ax" textAnchor="end">
				{pct ? `${Math.round(v * 100)}%` : fmtTick(v)}
			</text>
		</g>
	));
}

export function useHover() {
	const [at, setAt] = useState(null);
	return {at, enter: useCallback(i => setAt(i), []), leave: useCallback(() => setAt(null), [])};
}

export function HitBands({months, x, W, H, onEnter, onLeave}) {
	const w = W / months.length;
	return months.map((m, i) => (
		<rect key={m.month} x={x(i) - w / 2} y={0} width={w} height={H} className="hit"
			onMouseEnter={() => onEnter(i)} onMouseLeave={onLeave} />
	));
}

export function Tooltip({months, at, rows, x, W, title}) {
	if (at == null) return null;
	const m = months[at];
	const left = Math.min(Math.max((x(at) / W) * 100, 2), 62);
	return (
		<div className="tip" style={{left: `${left}%`, top: 4}}>
			<div className="tip__m">
				{title ? title(m) : m.month}{m.ledger_partial && <span className="pill">delvis</span>}
			</div>
			{rows(m).map(([k, v]) => (
				<div className="tip__r" key={k}><span>{k}</span><b>{v}</b></div>
			))}
		</div>
	);
}

export function Legend({items}) {
	return (
		<div className="legend">
			{items.map(it => (
				<span key={it.label}>
					{it.dash !== undefined
						? <svg width="18" height="8" aria-hidden="true"><line x1="0" y1="4" x2="18" y2="4"
							stroke="#000" strokeWidth="2" strokeDasharray={it.dash ?? undefined} /></svg>
						: <i className="sw" style={swatchStyle(it.fill)} />}
					{it.label}
				</span>
			))}
		</div>
	);
}

const swatchStyle = id => ({
	solid: {background: '#000'},
	hatch: {background: 'repeating-linear-gradient(45deg,#000 0 2px,#fff 2px 4px)', border: '1px solid #000'},
	cross: {background: 'repeating-linear-gradient(135deg,#000 0 1.5px,#fff 1.5px 4px)', border: '1px solid #000'},
	dots: {background: 'radial-gradient(#000 1px, #fff 1.2px)', backgroundSize: '4px 4px', border: '1px solid #000'},
	grey: {background: '#8a8a8a'},
	open: {background: '#fff', border: '1.5px solid #000'},
	light: {background: '#d9d9d9', border: '1px solid #c9c9c9'},
}[id] ?? {background: '#000'});
